import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Sequence, SequenceStore, User } from '../types';
import { 
  fetchPublicSequences, 
  savePublicSequence, 
  fetchUserSequences,
  saveUserSequence,
  deleteS3Object
} from '../services/sequenceService';
import { saveUserData, loadUserData } from '../services/userService';

export const useSequenceStore = create<SequenceStore>()(
  persist(
    (set, get) => ({
      sequences: [],
      selectedSequence: null,
      user: null,
      
      initializeStore: async () => {
        console.log('Initializing sequence store...');
        
        try {
          // Load local sequences first
          const storedSequences = localStorage.getItem('privateSequences');
          let localSequences: Sequence[] = [];
          
          if (storedSequences) {
            try {
              localSequences = JSON.parse(storedSequences);
              localSequences = localSequences.map(seq => ({
                ...seq,
                settings: {
                  ...seq.settings,
                  engine: seq.settings.engine || 'neural'
                },
                audioCache: seq.audioCache || {},
                isPublic: false
              }));
              console.log(`Loaded ${localSequences.length} local sequences`);
            } catch (error) {
              console.error('Error parsing local sequences:', error);
            }
          }

          // Load user data if available
          const storedUser = localStorage.getItem('barker_user');
          let userData: User | null = null;
          let userSequences: Sequence[] = [];
          
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              if (parsedUser && parsedUser.cloudFolder) {
                userData = await loadUserData(parsedUser.cloudFolder);
                if (userData) {
                  set({ user: userData });
                  userSequences = await fetchUserSequences(userData);
                  console.log(`Loaded ${userSequences.length} cloud sequences`);
                }
              }
            } catch (error) {
              console.error('Error loading user data:', error);
            }
          }

          // Fetch public sequences
          const publicSequences = await fetchPublicSequences();
          console.log(`Loaded ${publicSequences.length} public sequences`);
          
          // Combine all sequences, ensuring no duplicates
          const sequenceMap = new Map<string, Sequence>();
          
          // Add public sequences first
          publicSequences.forEach(seq => {
            sequenceMap.set(seq.id, {...seq, isPublic: true});
          });
          
          // Add user's cloud sequences
          userSequences.forEach(seq => {
            sequenceMap.set(seq.id, {...seq, isPublic: false});
          });
          
          // Add local sequences last (don't overwrite cloud sequences)
          localSequences.forEach(seq => {
            if (!sequenceMap.has(seq.id)) {
              sequenceMap.set(seq.id, {...seq, isPublic: false});
            }
          });
          
          const allSequences = Array.from(sequenceMap.values());
          set({ sequences: allSequences });
        } catch (error) {
          console.error('Error initializing store:', error);
          set({ sequences: [] });
        }
      },

      setUser: (user: User | null, isNewUser: boolean = false) => {
        if (user) {
          localStorage.setItem('barker_user', JSON.stringify(user));
          saveUserData(user, isNewUser);
          get().initializeStore();
        } else {
          localStorage.removeItem('barker_user');
          
          set((state) => {
            const storedSequences = localStorage.getItem('privateSequences');
            let localSequences: Sequence[] = [];
            
            if (storedSequences) {
              try {
                localSequences = JSON.parse(storedSequences);
                localSequences = localSequences.map(seq => ({
                  ...seq,
                  settings: {
                    ...seq.settings,
                    engine: seq.settings.engine || 'neural'
                  },
                  audioCache: seq.audioCache || {},
                  isPublic: false
                }));
              } catch (error) {
                console.error('Error parsing local sequences:', error);
              }
            }
            
            const publicSequences = state.sequences.filter(seq => seq.isPublic);
            const newSequences = [...publicSequences, ...localSequences];
            
            return {
              user: null,
              sequences: newSequences
            };
          });
        }
      },

      addSequence: (sequence) => {
        console.log(`Adding sequence: ${sequence.name} (${sequence.id})`);
        
        set((state) => {
          const existingIndex = state.sequences.findIndex(seq => seq.id === sequence.id);
          
          let newSequences;
          if (existingIndex >= 0) {
            newSequences = [...state.sequences];
            newSequences[existingIndex] = sequence;
          } else {
            newSequences = [...state.sequences, sequence];
          }
          
          if (sequence.isPublic) {
            savePublicSequence(sequence);
          } else if (state.user) {
            saveUserSequence(sequence, state.user);
          } else {
            const localSequences = newSequences.filter(seq => 
              !seq.isPublic && !state.user?.cloudFolder
            );
            localStorage.setItem('privateSequences', JSON.stringify(localSequences));
          }
          
          return { sequences: newSequences };
        });
      },

      deleteSequence: async (id) => {
        console.log(`Deleting sequence with ID: ${id}`);
        
        set((state) => {
          // Find the sequence to be deleted
          const sequenceToDelete = state.sequences.find(seq => seq.id === id);
          
          if (!sequenceToDelete) {
            console.log('Sequence not found');
            return state;
          }
          
          // If it's a cloud sequence, delete from S3
          if (sequenceToDelete.cloudId) {
            console.log('Deleting cloud sequence:', sequenceToDelete.cloudId);
            deleteS3Object(sequenceToDelete.cloudId)
              .then(success => {
                if (!success) {
                  console.error('Failed to delete sequence from S3');
                }
              })
              .catch(error => {
                console.error('Error deleting sequence from S3:', error);
              });
          }
          
          const newSequences = state.sequences.filter((seq) => seq.id !== id);
          
          // Only update localStorage for local sequences
          if (!state.user && !sequenceToDelete.cloudId) {
            const localSequences = newSequences.filter(seq => !seq.isPublic);
            localStorage.setItem('privateSequences', JSON.stringify(localSequences));
          }
          
          return {
            sequences: newSequences,
            selectedSequence: state.selectedSequence?.id === id ? null : state.selectedSequence,
          };
        });
      },

      updateSequence: (sequence, saveToStorage = true) => {
        console.log(`Updating sequence: ${sequence.name} (${sequence.id}), saveToStorage: ${saveToStorage}`);
        
        set((state) => {
          const existingSequence = state.sequences.find(seq => seq.id === sequence.id);
          const oldName = existingSequence?.name;
          
          const newSequences = state.sequences.map((seq) =>
            seq.id === sequence.id ? sequence : seq
          );
          
          if (saveToStorage) {
            if (sequence.isPublic) {
              savePublicSequence(sequence, oldName);
            } else if (state.user) {
              saveUserSequence(sequence, state.user, oldName);
            } else {
              const localSequences = newSequences.filter(seq => 
                !seq.isPublic && !state.user?.cloudFolder
              );
              localStorage.setItem('privateSequences', JSON.stringify(localSequences));
            }
          }
          
          return {
            sequences: newSequences,
            selectedSequence:
              state.selectedSequence?.id === sequence.id
                ? sequence
                : state.selectedSequence,
          };
        });
      },

      setSelectedSequence: (sequence) => {
        const sequenceCopy = sequence ? JSON.parse(JSON.stringify(sequence)) : null;
        set({ selectedSequence: sequenceCopy });
      },
    }),
    {
      name: 'barker-storage',
      partialize: (state) => ({
        user: state.user,
      }),
    }
  )
);